<template>
  <div class="user-thumbnail v-centerer" :class="classes" @click="editLeaderRadio">
    <EditableLabelRadio
      ref="radio-leader"
      class="user-thumbnail__label"
      :value="value"
      :items="users"
      label-key="label"
      :disabled="disabled"
      :no-text="!showName"
      :placeholder="placeholder"
      :stop-propagation="stopPropagation"
      :force-value-as-array="forceValueAsArray"
      @input="$emit('input', $event)"
      @startEdit="$emit('startEdit')"
      @close="$emit('endEdit')"
    />
    <div v-if="pictureTakerVisibility" class="dark-overlay h-v-centerer">
      <div class="dark-overlay-content xlarge">
        <PictureTaker
          @close="pictureTakerVisibility = false"
          @picture="pictureTaken"
        />
      </div>
    </div>
    <img
      v-if="statePicture !== ''"
      class="user-thumbnail__avatar user-thumbnail__avatar--img"
      :src="statePicture || placeholderImg"
      alt=""
    >
    <div
      v-if="statePicture === '' && user.firstName"
      class="user-thumbnail__avatar user-thumbnail__avatar--initials h-v-centerer"
    >
      {{ initials }}
    </div>

    <!-- Hidden components -->
    <input
      ref="fileInput"
      type="file"
      style="opacity: 0; position: absolute; z-index: -1; width: 0px;"
      accept=".png,.jpg,.bmp,.gif,.jpeg"
      size="0"
      @change="backFromBrowsing"
    >
  </div>
</template>

<script>
/**
 * Display a user picture and name
 *
 * @vue-prop {Number} v-model - Id of the user
 * @vue-prop {Array} allUsers - Array of users objects
 * @vue-prop {Boolean} disabled - Prevent edition
 * @vue-prop {Boolean} showName - Disaply user's name
 * @vue-prop {Boolean} fetchData - send an HTTP request to retrieve related user data
 * @vue-prop {String} placeholder - Text to use as an HTML placeholder attr
 * @vue-prop {Boolean} row - If true, make display inline
 * @vue-prop {Boolean} reverse - If true, Invert the position of the image and the text
 * @vue-prop {Boolean} editPictureOnClick - If true, clicking the thumbnail display a prompt to edit the picture
 * @vue-prop {Boolean} stopPropagation - Prevent the click event propagation when clicking on buttons
 * @vue-prop {Boolean} forceValueAsArray - Set value as an array instead of simple value
 *
 * @vue-event {Number|Number[]} input - Emits the edited value when an item is picked.
 *                                   If multiple is true, emits an array of numbers
 * @vue-event {} ok - OK button clicked
 * @vue-event {} close - Edition popup was closed
 *
 */

import $ from 'jquery';
import { cloneDeep, isEqual } from 'lodash';
// Vuex
import {
  FETCH_USER_DATA,
  FETCH_USER_PICTURE,
  EDIT_USER_PICTURE,
  DELETE_USER_PICTURE,
} from '@/store/action-types';
// Components
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import PictureTaker from '@/components/picture-taker.vue';
// Utils
import {
  blobToDataURL,
  handlePictureOrientation,
  handlePictureDimension,
} from '@/utils/file';
import { calert } from '@/utils/main';

const placeholderImg = require('@/assets/img/user-pict-placeholder.png');

export default {

  name: 'UserThumbnail',

  components: {
    EditableLabelRadio,
    PictureTaker,
  },

  props: {
    value: {
      type: Number,
      required: true,
    },
    allUsers: {
      type: Array,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: Boolean,
    showName: Boolean,
    fetchData: Boolean,
    row: Boolean,
    wrap: Boolean,
    reverse: Boolean,
    editPictureOnClick: Boolean,
    stopPropagation: Boolean,
    forceValueAsArray: Boolean,
  },

  data() {
    return {
      placeholderImg,
      pictureTakerVisibility: false,
    };
  },

  computed: {
    classes() {
      return {
        'user-thumbnail--row': !!this.row,
        'user-thumbnail--wrap': !!this.wrap,
        'user-thumbnail--reverse': !!this.reverse,
        'user-thumbnail--disabled': !!this.disabled,
        'user-thumbnail--show-name': !!this.showName,
      };
    },
    users() {
      if (this.allUsers instanceof Array) {
        return this.allUsers;
      }
      return (this.fetchData && this.stateUser) ? [this.stateUser] : [];
    },
    user() {
      if (this.fetchData && this.stateUser) {
        return this.stateUser;
      }
      for (let i = 0, len = this.users.length; i < len; i++) {
        if (Number(this.users[i].id) === Number(this.value)) {
          return cloneDeep(this.users[i]);
        }
      }
      return {
        firstName: '',
        lastName: '',
        picture: '',
      };
    },
    initials() {
      const { firstName, lastName } = this.user;
      return typeof firstName === 'string' ? `${firstName.substring(0, 1)}${lastName.substring(0, 1)}` : '';
    },
    /** Data from state */
    stateUserData() {
      return this.$store.state.userData[`${this.value}`] || {};
    },
    stateUser() {
      return this.stateUserData.user;
    },
    statePicture() {
      return this.stateUserData.pictureData;
    },
  },

  watch: {
    allUsers(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.init();
      }
    },
    value(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.init();
      }
    },
  },

  mounted() {
    this.init();
  },

  methods: {
    /** Initialize the component */
    init() {
      if (this.fetchData && Number(this.value) && !this.stateUserData.loading) {
        this.$store.dispatch(FETCH_USER_DATA, { id: this.value }).then(() => {
          if (!this.statePicture && !this.stateUserData.pictureLoading) {
            this.$store.dispatch(FETCH_USER_PICTURE, { id: this.value });
          }
        });
      }
    },
    /** Handle click on user picture */
    editLeaderRadio(event) {
      if (!this.stopPropagation) {
        event.stopPropagation();
      }
      if (!this.disabled) {
        this.$refs['radio-leader'].edit();
      } else if (this.editPictureOnClick) {
        this.editUserPicture();
      }
    },
    /** If editPictureOnClick, clicking the picture triggers the edition modal */
    editUserPicture() {
      const options = [
        { title: this.$t('message.TAKE_PICTURE'), action: this.takePicture },
        { title: this.$t('message.BROWSE_GALLERY'), action: this.browseGallery },
      ];
      if (this.statePicture) {
        options.push({
          title: this.$t('message.DELETE'),
          sClass: 'red',
          action: this.deleteUserPicture,
        });
      }
      calert(this.$t('message.HOW_ADD_PICTURE'), options);
    },
    takePicture() {
      this.pictureTakerVisibility = true;
    },
    pictureTaken(blob) {
      this.pictureTakerVisibility = false;
      this.setUserPicture(blob);
    },
    browseGallery() {
      $(this.$refs.fileInput).trigger('click');
    },
    backFromBrowsing() {
      const file = this.$refs.fileInput.files[0];
      this.setUserPicture(file);
    },
    setUserPicture(blob) {
      return handlePictureOrientation(blob)
        .then(blobOriented => handlePictureDimension(blobOriented, 300, 300))
        .then(blobResized => blobToDataURL(blobResized))
        .then((dataURL) => {
          this.$store.dispatch(EDIT_USER_PICTURE, { userId: this.value, picture: dataURL });
        });
    },
    deleteUserPicture() {
      this.$store.dispatch(DELETE_USER_PICTURE, { userId: this.value });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";


.user-thumbnail {
  cursor: pointer;

  &--row {
    display: inline-flex;

    .user-thumbnail__label {
      align-items: center;
      display: flex;
    }
  }

  &--show-name {
    .user-thumbnail__avatar {
      margin: 0 0 0 .7rem;
    }
  }

  &--wrap {
    &.v-centerer {
      flex-flow: column-reverse;

      &.user-thumbnail--show-name {
        .user-thumbnail__avatar {
          margin: 0 0 .3rem 0;
        }
      }

      .editable-label {
        text-align: center;
      }
    }
  }

  &--reverse {
    &.v-centerer {
      flex-flow: row-reverse;
      justify-content: flex-end;

      .user-thumbnail__avatar {
        margin: 0 0.7rem 0 0;
      }
    }
  }

  &__avatar {
    flex: 0 0 auto;
    width: 1.8rem;
    height: 1.8rem;
    margin: auto;
    max-width: 100%;
    max-height: 100%;

    &--img {
      display: block;
      object-fit: cover;
      border-radius: 100px;
    }
    &--initials {
      background: #BDBEC0;
      border-radius: 100px;
    }
  }

  .editable-label {
    text-align: right;
    min-width: 0px;

    div.textarea, .editable-label-text {
      min-height: auto;
    }
  }
}
</style>
